/**
 * DebugMenuPage — `/dev/debug-menu`.
 *
 * 진입 조건: useDebugStore.unlocked === true.
 *   (false 면 RequireDebugUnlock 가드가 내 정보(/me)로 redirect)
 *
 * 패널:
 *   1. API Base — 백엔드 prefix 런타임 변경 (이 PR 의 메인 목적)
 *   2. Push Token — 현재 등록된 토큰 표시 + 클립보드 복사
 *   3. OTA — 현재 OTA 버전 + 강제 체크 트리거
 *   4. Logs — 최근 console 로그 dump (메모리 ring buffer)
 *   5. Crash — 강제 throw (Sentry 검증)
 *
 * 스타일은 dev 도구라 minimal — 디자인 시스템 따로 안 맞춤.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDebugStore } from '../../store/debugStore';
import { useAuthStore } from '../../store/authStore';
import {
  API_BASE_PRESETS,
  isApiOverrideAllowed,
  env,
  getApiBaseOverride,
  getEffectiveApiBase,
  setApiBaseOverride,
  VITE_MODE,
} from '../../config/env';
import { getCurrentOtaVersion, checkForOtaUpdate } from '../../native/liveUpdate';
import { isNative, getPlatform } from '../../native';
import { getNativeAppInfo } from '../../native/app';
import { getPushDebugSnapshot, type PushDebugSnapshot } from '../../native/fcmPush';
import {
  acquireAsset,
  releaseAssetLease,
  syncAssets,
  toAssetErrorCode,
  type AssetLease,
} from '../../native/assetDelivery';
import { useAssetSyncStore } from '../../store/assetSyncStore';
import { debugApi, type SystemConfigSnapshotEntry } from '../../api/debug';
import styles from './DebugMenuPage.module.css';

export default function DebugMenuPage() {
  const navigate = useNavigate();
  const lock = useDebugStore((s) => s.lock);

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <button
          type="button"
          className={styles.backBtn}
          onClick={() => navigate(-1)}
          aria-label="뒤로"
        >
          ←
        </button>
        <h1 className={styles.title}>🔧 디버그 메뉴</h1>
        <button
          type="button"
          className={styles.lockBtn}
          onClick={() => {
            lock();
            navigate('/me', { replace: true });
          }}
        >
          잠금
        </button>
      </header>

      <PreviewReadyPanel />
      <ScanToolsPanel />
      <BuildInfoPanel />
      <AssetReleasePanel />
      {/* 운영 빌드는 서버 전환이 막혀 있다(env.isApiOverrideAllowed) — 눌러도 안 되는 버튼을 두지 않는다 */}
      {isApiOverrideAllowed() && <ApiBasePanel />}
      <PushRegistrationPanel />
      <OtaPanel />
      <SystemConfigPanel />
      <LogsPanel />
      <CrashPanel />

      <p className={styles.footer}>
        platform: <code>{getPlatform()}</code> · native: <code>{String(isNative())}</code>
      </p>
    </div>
  );
}

// ============================================================
// 0. Build / 배포 버전
// ============================================================

/** vite.config.ts define 으로 주입되는 빌드 상수 (git short sha / build 시각). */
const BUILD_VERSION = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '(unknown)';
const BUILD_AT = typeof __APP_BUILT_AT__ !== 'undefined' ? __APP_BUILT_AT__ : '(unknown)';

function formatBuiltAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

function BuildInfoPanel() {
  const [nativeInfo, setNativeInfo] = useState<{ version: string; build: string } | null>(null);

  useEffect(() => {
    void getNativeAppInfo().then(setNativeInfo);
  }, []);

  const nativeLabel = nativeInfo
    ? `${nativeInfo.version} (vc${nativeInfo.build})`
    : '(web / 미조회)';

  const copy = () => {
    const text = [
      `version: ${BUILD_VERSION}`,
      `builtAt: ${BUILD_AT}`,
      `mode: ${VITE_MODE}`,
      `channel: ${env.VITE_OTA_CHANNEL}`,
      `ota: ${getCurrentOtaVersion() ?? '(native)'}`,
      `nativeApp: ${nativeLabel}`,
    ].join('\n');
    navigator.clipboard?.writeText(text);
    alert('배포 정보 복사됨');
  };

  return (
    <Section title="⓪ Build / 배포 버전">
      <div className={styles.kv}>
        <span>native versionCode</span>
        <code className={styles.code}>{nativeLabel}</code>
      </div>
      <div className={styles.kv}>
        <span>build version</span>
        <code className={styles.code}>{BUILD_VERSION}</code>
      </div>
      <div className={styles.kv}>
        <span>built at</span>
        <code className={styles.code}>{formatBuiltAt(BUILD_AT)}</code>
      </div>
      <div className={styles.kv}>
        <span>mode</span>
        <code className={styles.code}>{VITE_MODE}</code>
      </div>
      <div className={styles.kv}>
        <span>OTA channel</span>
        <code className={styles.code}>{env.VITE_OTA_CHANNEL}</code>
      </div>
      <div className={styles.btnRow}>
        <button type="button" className={styles.btn} onClick={copy}>
          배포 정보 복사
        </button>
      </div>
      <p className={styles.hint}>
        * build version = 빌드 시점 git short SHA (CI 는 GITHUB_SHA). OTA 번들 버전은 ③ 패널 참고.
      </p>
    </Section>
  );
}

// ============================================================
// 인식 에셋 릴리스 (asset-delivery)
// ============================================================

/**
 * 점검할 kind — 스트림별 릴리스 매핑 계약과 1:1.
 *
 * ⚠ **manifest 가 아니라 기기에 놓인 파일**을 본다. lease 를 잡으면 지금 스캔이
 * 실제로 열게 될 그 파일의 세대가 나오므로, "내가 지금 무슨 릴리스를 쓰고 있나" 에
 * 대한 답이 된다(서버 활성 포인터와 다를 수 있다 — sync 전이거나 실패했을 때).
 *
 * kind 가 빠진 릴리스가 나가면 그 기능이 조용히 죽는다(2026-08-04 실사고: matcher
 * 5종 누락으로 카드 식별 불능). 그래서 여기서는 스트림 전체를 훑고, 못 받은 kind 는
 * 오류 코드로 드러낸다.
 */
const ASSET_KIND_GROUPS = [
  { stream: 'liner', kinds: ['card-seg', 'card-refine', 'card-verifier', 'card-config'] },
  {
    stream: 'matcher',
    kinds: [
      'card-embed',
      'card-embed-data',
      'card-index',
      'card-serlno',
      'card-matcher-config',
      // 선택 kind — 없는 릴리스에서는 NOT_READY 로 뜨는 것이 정상이다
      'card-meta',
    ],
  },
] as const;

interface AssetRow {
  stream: string;
  kind: string;
  version?: string;
  releaseId?: number;
  hash?: string;
  protection?: string;
  error?: string;
}

/** 한 스트림의 kind 들이 같은 세대인지 — 다르면 섞인 것이다(정리 실패/부분 sync). */
function streamVersionLabel(rows: AssetRow[]): string {
  const versions = [...new Set(rows.filter((r) => r.version).map((r) => r.version!))];
  if (versions.length === 0) return '(없음)';
  if (versions.length === 1) return versions[0];
  return `⚠ 세대 혼합: ${versions.join(' / ')}`;
}

function AssetReleasePanel() {
  const syncStatus = useAssetSyncStore((s) => s.status);
  const syncReleaseVer = useAssetSyncStore((s) => s.releaseVer);
  const syncErrorCode = useAssetSyncStore((s) => s.errorCode);
  const [rows, setRows] = useState<AssetRow[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const inspect = async () => {
    setLoading(true);
    const out: AssetRow[] = [];
    for (const group of ASSET_KIND_GROUPS) {
      for (const kind of group.kinds) {
        let lease: AssetLease | null = null;
        try {
          lease = await acquireAsset(kind);
          out.push({
            stream: group.stream,
            kind,
            version: lease.version,
            releaseId: lease.releaseId,
            hash: lease.hash,
            protection: lease.protection ?? 'NONE',
          });
        } catch (err) {
          out.push({
            stream: group.stream,
            kind,
            error: toAssetErrorCode(err) ?? (err as Error)?.message ?? 'unknown',
          });
        } finally {
          // lease 를 안 반납하면 그 세대가 정리 대상에서 빠져 디스크가 회수되지 않는다
          if (lease) await releaseAssetLease(lease.leaseId);
        }
      }
    }
    setRows(out);
    setLoading(false);
  };

  // 진입 시 1회 자동 조회 — 이 패널을 여는 이유가 곧 이 값이다
  useEffect(() => {
    void inspect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const forceSync = async () => {
    setSyncing(true);
    try {
      await syncAssets();
      await inspect();
    } finally {
      setSyncing(false);
    }
  };

  const copy = () => {
    if (!rows) return;
    const text = [
      `sync: ${syncStatus}${syncErrorCode ? ` (${syncErrorCode})` : ''}`,
      `syncReleaseVer: ${syncReleaseVer ?? '(없음)'}`,
      ...rows.map((r) =>
        r.error
          ? `${r.stream}/${r.kind}: ERROR ${r.error}`
          : `${r.stream}/${r.kind}: ${r.version} (rel ${r.releaseId}, ${r.protection}, ${r.hash?.slice(0, 12)})`,
      ),
    ].join('\n');
    navigator.clipboard?.writeText(text);
    alert('에셋 릴리스 정보 복사됨');
  };

  return (
    <Section title="🎴 인식 에셋 릴리스 (asset-delivery)">
      <p className={styles.hint}>
        * 지금 이 기기가 <b>실제로 쓰는</b> 모델 세대입니다(서버 활성 포인터가 아니라 받아둔 파일).
        스캔이 이상하면 여기 버전부터 확인하세요.
      </p>

      {rows &&
        ASSET_KIND_GROUPS.map((group) => {
          const groupRows = rows.filter((r) => r.stream === group.stream);
          return (
            <div className={styles.kv} key={group.stream}>
              <span>{group.stream} 릴리스</span>
              <code className={styles.code}>{streamVersionLabel(groupRows)}</code>
            </div>
          );
        })}

      <div className={styles.kv}>
        <span>마지막 sync</span>
        <code className={syncErrorCode ? styles.codeOverride : styles.code}>
          {syncStatus}
          {syncErrorCode ? ` (${syncErrorCode})` : ''}
        </code>
      </div>
      <div className={styles.kv}>
        <span>sync 결과 버전</span>
        <code className={styles.code}>{syncReleaseVer ?? '(미실행)'}</code>
      </div>

      <div className={styles.btnRow}>
        <button type="button" className={styles.btn} onClick={inspect} disabled={loading}>
          {loading ? '조회 중…' : '새로고침'}
        </button>
        <button type="button" className={styles.btn} onClick={forceSync} disabled={syncing}>
          {syncing ? '동기화 중…' : '강제 재동기화'}
        </button>
        <button type="button" className={styles.btn} onClick={copy} disabled={!rows}>
          복사
        </button>
      </div>

      <div className={styles.subTitle}>kind 별 상세</div>
      <pre className={styles.pre}>
        {!rows
          ? '(조회 중…)'
          : rows
              .map((r) =>
                r.error
                  ? `${r.kind}\n  ✖ ${r.error}`
                  : `${r.kind}\n  ${r.version} · rel ${r.releaseId} · ${r.protection} · ${r.hash?.slice(0, 12)}`,
              )
              .join('\n')}
      </pre>
      <p className={styles.hint}>
        * <code>NOT_READY</code> = 아직 안 받은 kind(그 기능이 죽어 있다는 뜻).
        웹에서는 전부 NOT_READY 가 정상입니다 — 모델은 네이티브 전용.<br />
        * <code>AES_GCM_V1</code> = DFC1 암호문(엔진이 메모리에서만 복호화).
      </p>
    </Section>
  );
}

// ============================================================
// 1. API Base
// ============================================================

function ApiBasePanel() {
  const [override, setOverride] = useState(getApiBaseOverride() ?? '');
  const [custom, setCustom] = useState('');
  const [error, setError] = useState<string | null>(null);
  const effective = getEffectiveApiBase();
  const isOverridden = getApiBaseOverride() !== null;

  const apply = (url: string) => {
    setError(null);
    const trimmed = url.trim();
    if (!trimmed) {
      setError('URL 을 입력해주세요');
      return;
    }
    // 상대 경로 (Vite proxy) 또는 절대 URL 허용
    if (!trimmed.startsWith('/') && !/^https?:\/\//.test(trimmed)) {
      setError('형식이 올바르지 않아요 (http(s):// 또는 / 로 시작)');
      return;
    }
    setApiBaseOverride(trimmed);
    setOverride(trimmed);
    // 강제 로그아웃 — 다른 BE 의 토큰은 무효
    useAuthStore.getState().clear();
    alert(`백엔드 변경됨:\n${trimmed}\n\n홈으로 이동합니다.`);
    // 홈으로. 인증 필요한 화면 진입 시 RequireAuth 가 auth activity 띄움.
    window.location.href = '/';
  };

  const reset = () => {
    setApiBaseOverride(null);
    setOverride('');
    useAuthStore.getState().clear();
    alert('초기화 — env 값으로 복귀\n홈으로 이동합니다.');
    window.location.href = '/';
  };

  return (
    <Section title="① API Base (백엔드 prefix)">
      <div className={styles.kv}>
        <span>현재 effective</span>
        <code className={isOverridden ? styles.codeOverride : styles.code}>{effective}</code>
      </div>
      <div className={styles.kv}>
        <span>env 빌드값</span>
        <code className={styles.code}>{env.VITE_API_BASE_URL}</code>
      </div>
      <div className={styles.kv}>
        <span>override</span>
        <code className={styles.code}>{override || '(없음)'}</code>
      </div>

      <div className={styles.subTitle}>Preset (누르면 아래 Custom URL 에 채워짐)</div>
      <div className={styles.btnRow}>
        {API_BASE_PRESETS.map((p) => (
          <button
            key={p.url}
            type="button"
            className={styles.btn}
            onClick={() => {
              setCustom(p.url);
              setError(null);
            }}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className={styles.subTitle}>Custom URL</div>
      <input
        type="text"
        className={styles.input}
        value={custom}
        onChange={(e) => setCustom(e.target.value)}
        placeholder="https://example.com/api/v1"
        spellCheck={false}
      />
      {error && <span className={styles.error}>{error}</span>}
      <div className={styles.btnRow}>
        <button type="button" className={styles.btnPrimary} onClick={() => apply(custom)}>
          적용 (강제 로그아웃)
        </button>
        <button type="button" className={styles.btnDanger} onClick={reset}>
          초기화
        </button>
      </div>

      <p className={styles.hint}>
        * 적용 / 초기화 시 인증 토큰 무효화를 위해 강제 로그아웃됩니다.<br />
        * OAuth redirect URI 는 빌드 타임 값 — BE 만 바꾸면 OAuth flow 가 깨질 수 있어요.
      </p>
    </Section>
  );
}

// ============================================================
// 판매준비상태 함께 보기 (preview)
// ============================================================

function PreviewReadyPanel() {
  const previewReady = useDebugStore((s) => s.previewReady);
  const setPreviewReady = useDebugStore((s) => s.setPreviewReady);

  return (
    <Section title="🛒 판매준비상태 함께 보기">
      <label className={styles.kv} style={{ cursor: 'pointer' }}>
        <span>전체목록·상세에 READY 노출</span>
        <input
          type="checkbox"
          checked={previewReady}
          onChange={(e) => setPreviewReady(e.target.checked)}
        />
      </label>
      <p className={styles.hint}>
        * 켜면 쿠지/가챠 <b>'전체' 탭</b>과 <b>상세</b>에 판매준비(READY) 상품이 노출됩니다
        (READY 가 목록 최상단). 발매 전 상품 이미지가 실제 카드/상세에서 어떻게 보이는지 확인용.<br />
        * 구매·참여는 계속 차단됩니다. 디버그 <b>잠금</b> 시 자동 해제.
      </p>
    </Section>
  );
}

// ============================================================
// 스캔 화면 개발 도구 (라벨링 / 진단줄)
// ============================================================

/**
 * 스캔 화면의 개발자용 표시를 여기서만 켠다 (2026-08-28 사용자 결정).
 * 종전엔 빌드 모드(비운영)로 갈라 dev 빌드에서는 항상 떠 있었다 — 실기기로
 * 인식 품질을 볼 때 진단줄·라벨링 버튼이 계속 화면을 덮었다.
 */
function ScanToolsPanel() {
  const scanLabeling = useDebugStore((s) => s.scanLabeling);
  const setScanLabeling = useDebugStore((s) => s.setScanLabeling);
  const scanDiagnostics = useDebugStore((s) => s.scanDiagnostics);
  const setScanDiagnostics = useDebugStore((s) => s.setScanDiagnostics);

  return (
    <Section title="🎴 스캔 화면 개발 도구">
      <label className={styles.kv} style={{ cursor: 'pointer' }}>
        <span>라벨링 모드 (모드 전환 버튼 · 라벨 수집 UI)</span>
        <input
          type="checkbox"
          checked={scanLabeling}
          onChange={(e) => setScanLabeling(e.target.checked)}
        />
      </label>
      <label className={styles.kv} style={{ cursor: 'pointer' }}>
        <span>진단줄 (vote/sim/sharp · 라이브·스틸·확정 사유)</span>
        <input
          type="checkbox"
          checked={scanDiagnostics}
          onChange={(e) => setScanDiagnostics(e.target.checked)}
        />
      </label>
      <p className={styles.hint}>
        * 끄면 스캔 화면은 <b>소비자가 보는 그대로</b>입니다 (기본값 off).<br />
        * 라벨링을 끄면 라벨링 모드에 있었더라도 카드 스캔 모드로 돌아갑니다.<br />
        * 디버그 <b>잠금</b> 시 자동 해제.
      </p>
    </Section>
  );
}

// ============================================================
// 푸시 등록 정보 (FCM/APNs 직발송)
// ============================================================

/** 도달 가능 = 네이티브 + OS 권한 + 토큰 발급 + BE 기기 등록. 하나라도 비면 안 닿는다. */
function isMessageable(s: PushDebugSnapshot): boolean {
  return s.native && s.permissionGranted && Boolean(s.pushToken) && Boolean(s.deviceSerialNo);
}

function PushRegistrationPanel() {
  const [snap, setSnap] = useState<PushDebugSnapshot | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchSnap = async () => {
    setLoading(true);
    try {
      setSnap(await getPushDebugSnapshot());
    } finally {
      setLoading(false);
    }
  };

  // 진입 시 1회 자동 조회.
  useEffect(() => {
    void fetchSnap();
  }, []);

  const copy = () => {
    if (!snap) return;
    navigator.clipboard?.writeText(JSON.stringify(snap, null, 2));
    alert('푸시 등록 정보 복사됨');
  };

  const messageable = snap ? isMessageable(snap) : false;

  return (
    <Section title="푸시 등록 정보">
      <p className={styles.hint}>
        * 푸시 동의 플래그(설정)와 별개로 <b>실제 등록 상태</b>. 발송이 도달 안 될 때 어느 단계가
        비어있는지 확인용. 게이트웨이는 아래 pushToken 을 타겟으로 직접 쏩니다.
      </p>
      <div className={styles.btnRow}>
        <button type="button" className={styles.btn} onClick={fetchSnap} disabled={loading}>
          {loading ? '조회 중…' : '새로고침'}
        </button>
        <button type="button" className={styles.btn} onClick={copy} disabled={!snap}>
          복사
        </button>
      </div>
      {snap && (
        <>
          <div className={styles.kv}>
            <span>도달 가능</span>
            <code className={messageable ? styles.code : styles.codeOverride}>
              {messageable ? 'YES (messageable)' : 'NO — 도달 안 됨'}
            </code>
          </div>
          <div className={styles.kv}>
            <span>native</span>
            <code className={styles.code}>{String(snap.native)}</code>
          </div>
          <div className={styles.kv}>
            <span>리스너 bootstrap</span>
            <code className={styles.code}>{String(snap.bootstrapped)}</code>
          </div>
          <div className={styles.kv}>
            <span>OS 권한 granted</span>
            <code className={styles.code}>
              {String(snap.permissionGranted)}
              {snap.canRequestPermission ? ' (canAsk)' : ''}
            </code>
          </div>
          <div className={styles.kv}>
            <span>pushToken</span>
            <code className={styles.code}>{snap.pushToken ?? '(없음)'}</code>
          </div>
          <div className={styles.kv}>
            <span>installationId (기기 키)</span>
            <code className={styles.code}>{snap.installationId ?? '(없음)'}</code>
          </div>
          <div className={styles.kv}>
            <span>deviceSerialNo</span>
            <code className={styles.code}>{snap.deviceSerialNo ?? '(미등록)'}</code>
          </div>
          <div className={styles.kv}>
            <span>device 동의 Yn</span>
            <code className={styles.code}>{snap.deviceAgreementYn ?? '(미등록)'}</code>
          </div>
        </>
      )}
      {!snap && !loading && <pre className={styles.pre}>(조회 안 됨)</pre>}
      <p className={styles.hint}>
        * deviceSerialNo 는 이 세션에서 <code>registerPushDevice</code>(로그인·권한 ON)가 성공해야
        채워집니다. 앱 재시작 후엔 재로그인 트리거 전까지 비어 보일 수 있어요.
      </p>
    </Section>
  );
}

// ============================================================
// System Config (전파 검증)
// ============================================================

function SystemConfigPanel() {
  const [entries, setEntries] = useState<SystemConfigSnapshotEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchSnapshot = async () => {
    setLoading(true);
    setError(null);
    try {
      const snap = await debugApi.systemConfigs();
      setEntries(snap.configs);
    } catch (err) {
      // debug.enabled=false 면 403. JWT 만료 시 401.
      setError((err as Error).message ?? '조회 실패 (debug.enabled 또는 인증 확인)');
      setEntries(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Section title="System Config (전파 검증)">
      <p className={styles.hint}>
        BE 가 현재 resolve 하는 설정값. 어드민에서 값 변경 후 여기서 반영 여부 확인.
      </p>
      <div className={styles.btnRow}>
        <button type="button" className={styles.btn} onClick={fetchSnapshot} disabled={loading}>
          {loading ? '조회 중…' : 'BE 설정값 조회'}
        </button>
      </div>
      {error && <pre className={styles.pre}>error: {error}</pre>}
      {entries && (
        <pre className={styles.pre}>
          {entries
            .map((e) => `${e.code} = ${e.value ?? `(error: ${e.error ?? 'unknown'})`}`)
            .join('\n')}
        </pre>
      )}
      {!entries && !error && <pre className={styles.pre}>(아직 조회 안 함)</pre>}
    </Section>
  );
}

// ============================================================
// 3. OTA
// ============================================================

function OtaPanel() {
  const [current, setCurrent] = useState<string | null>(null);
  const [latest, setLatest] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    setCurrent(getCurrentOtaVersion());
  }, []);

  const check = async () => {
    setChecking(true);
    try {
      const p = await checkForOtaUpdate();
      if (!p || (!p.updateAvailable && !p.storeUpdateRequired)) {
        setLatest('(업데이트 없음)');
      } else if (p.storeUpdateRequired) {
        setLatest('스토어 업데이트 필요 (네이티브 낡음)');
      } else {
        setLatest(`${p.version} (${p.mode})`);
      }
    } catch (err) {
      setLatest(`error: ${(err as Error).message}`);
    } finally {
      setChecking(false);
    }
  };

  return (
    <Section title="③ OTA Live Update">
      <div className={styles.kv}>
        <span>current</span>
        <code className={styles.code}>{current ?? 'v1.0.0 (native)'}</code>
      </div>
      <div className={styles.kv}>
        <span>latest</span>
        <code className={styles.code}>{latest ?? '(미조회)'}</code>
      </div>
      <div className={styles.btnRow}>
        <button type="button" className={styles.btn} onClick={check} disabled={checking}>
          {checking ? '체크 중…' : '강제 체크'}
        </button>
      </div>
    </Section>
  );
}

// ============================================================
// 4. Logs (in-memory ring buffer)
// ============================================================

interface LogLine {
  ts: number;
  level: 'log' | 'warn' | 'error';
  msg: string;
}

const LOG_BUFFER: LogLine[] = [];
const LOG_MAX = 200;
let logHooked = false;

function hookConsole() {
  if (logHooked) return;
  logHooked = true;
  const orig = {
    log: console.log,
    warn: console.warn,
    error: console.error,
  };
  const push = (level: LogLine['level']) =>
    (...args: unknown[]) => {
      try {
        const msg = args
          .map((a) => (typeof a === 'string' ? a : safeStringify(a)))
          .join(' ');
        LOG_BUFFER.push({ ts: Date.now(), level, msg });
        if (LOG_BUFFER.length > LOG_MAX) LOG_BUFFER.shift();
      } catch {
        // ignore
      }
      orig[level].apply(console, args);
    };
  console.log = push('log');
  console.warn = push('warn');
  console.error = push('error');
}

function safeStringify(v: unknown): string {
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

function LogsPanel() {
  const [, force] = useState(0);

  useEffect(() => {
    hookConsole();
    const t = setInterval(() => force((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const dump = () => {
    const text = LOG_BUFFER.map(
      (l) => `[${new Date(l.ts).toLocaleTimeString()}][${l.level}] ${l.msg}`,
    ).join('\n');
    navigator.clipboard?.writeText(text);
    alert(`${LOG_BUFFER.length}개 로그 복사됨`);
  };

  const clear = () => {
    LOG_BUFFER.length = 0;
    force((n) => n + 1);
  };

  return (
    <Section title="④ Logs (ring buffer 200)">
      <div className={styles.btnRow}>
        <button type="button" className={styles.btn} onClick={dump}>
          클립보드 복사
        </button>
        <button type="button" className={styles.btn} onClick={clear}>
          비우기
        </button>
      </div>
      <pre className={styles.preLogs}>
        {LOG_BUFFER.length === 0
          ? '(로그 없음 — hook 부착 후 console 호출 시 누적)'
          : LOG_BUFFER.slice(-30)
              .map((l) => `[${l.level}] ${l.msg}`)
              .join('\n')}
      </pre>
    </Section>
  );
}

// ============================================================
// 5. Force Crash
// ============================================================

function CrashPanel() {
  const throwSync = () => {
    throw new Error(`[debug] forced sync error @ ${new Date().toISOString()}`);
  };
  const throwAsync = async () => {
    setTimeout(() => {
      throw new Error(`[debug] forced async error @ ${new Date().toISOString()}`);
    }, 0);
  };
  return (
    <Section title="⑤ Force Crash (Sentry 검증용)">
      <div className={styles.btnRow}>
        <button type="button" className={styles.btnDanger} onClick={throwSync}>
          sync throw
        </button>
        <button type="button" className={styles.btnDanger} onClick={throwAsync}>
          async throw
        </button>
      </div>
      <p className={styles.hint}>* ErrorBoundary 또는 Sentry handler 가 잡는지 확인용.</p>
    </Section>
  );
}

// ============================================================
// Section helper
// ============================================================

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>{title}</h2>
      <div className={styles.sectionBody}>{children}</div>
    </section>
  );
}
